import React from "react";
import styled from "styled-components";
import {
  brand_orange_deep,
  brand_danger,
  narrowScreenMediaQuery,
} from "../../constants/styles";
import { apiGet, apiPost } from "../../api";
import { reportPlayerError } from "../../utils/reportPlayerError";
import LessonPosterOverlay from "./LessonPosterOverlay";
import LessonProgressChip from "./LessonProgressChip";

// Inline Video.js player for the rebranded
// `/sertifikasi/:course/:curriculum/:lesson` Inertia page.
//
// Feature parity with the legacy `app/javascript/controllers/videos/player_controller.js`:
//   - Resume from `recording.resume_at_seconds` on metadata-loaded.
//   - 5-second watch-time tracking — fires `apiGet(timestamp_post_url)` with
//     the legacy header-based payload (timestamp/duration/starttime/endtime).
//     Throttled to multiples of 5 to match `UpdateTimestampWorker`'s gating.
//   - Watched-bar overlay rendered INSIDE Video.js's `.vjs-progress-control`
//     so 5-second windows the user has actually watched are visible as
//     brand-orange marks within the seek bar (YouTube-style heatmap).
//   - Jump-list panel — clickable timestamps parsed from `Video#timestamps`.
//   - Branded poster + big play button overlay (idle / ended states).
//   - Subscription gate when `can_watch === false`.
//   - Optional auto-advance via `onLessonEnded` callback.
//   - Keyboard hotkeys (Space play/pause, J -10s, L +10s, M mute, F fullscreen).
//
// Progress display is server-authoritative: `lessonProgressPercent` and
// `watchedTimes` are passed in from the parent (lifted state seeded from
// Inertia props and updated on every successful tick via `onTickResponse`).
// The parent applies the new values from the tick's response body —
// computed by the same backend methods the LessonDetailPresenter uses on
// initial render — so the chip mirrors the deduplicated server total
// instead of running a parallel client-side accumulator.
function LessonRecording({
  recording,
  canWatch = true,
  membershipsUrl = "/memberships",
  onLessonEnded,
  lessonProgressPercent,
  watchedTimes,
  onTickResponse,
}) {
  const videoElRef = React.useRef(null);
  const playerRef = React.useRef(null);
  const watchedBarHostRef = React.useRef(null);
  const firedLogRef = React.useRef(false);
  const lastReportedSecondRef = React.useRef(-1);
  const lastTickedSecondRef = React.useRef(-1);
  const [errorState, setErrorState] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [retryNonce, setRetryNonce] = React.useState(0);
  const [repaintNonce, setRepaintNonce] = React.useState(0);
  const [playerState, setPlayerState] = React.useState("idle"); // idle | playing | paused | ended
  // Throttled "current second" for chapter-highlight tracking. Updates at
  // most once per wall-clock second; cheaper than re-rendering on every
  // `timeupdate` (~4× / second).
  const [currentSeconds, setCurrentSeconds] = React.useState(0);

  const durationSeconds = Number(recording?.duration_seconds) || 0;

  // Wipe the watched-bar host on lesson navigation so stale segments
  // don't briefly flash before the parent's lifted state catches up.
  React.useEffect(() => {
    if (watchedBarHostRef.current) {
      try { watchedBarHostRef.current.replaceChildren(); } catch (_) { /* harmless */ }
    }
  }, [recording?.id]);

  const jumplist = recording?.timestamps_jumplist || [];

  React.useEffect(() => {
    if (!canWatch) return undefined;
    if (!recording || !videoElRef.current) return undefined;
    if (typeof window === "undefined") return undefined;

    let cancelled = false;
    let player;

    setErrorState(null);
    setLoading(true);
    firedLogRef.current = false;
    lastReportedSecondRef.current = -1;

    (async () => {
      try {
        const [{ default: videojs }] = await Promise.all([
          import("video.js"),
          import("video.js/dist/video-js.css"),
          import("videojs-contrib-quality-levels"),
          import("videojs-hls-quality-selector"),
        ]);
        if (cancelled || !videoElRef.current) return;

        player = videojs(videoElRef.current, {
          controls: true,
          fluid: true,
          playbackRates: [0.5, 1, 1.5, 2],
          // Custom keyboard shortcuts, mirroring YouTube conventions:
          //   Space      play / pause
          //   F          toggle fullscreen
          //   M          toggle mute
          //   J / ←      seek -10s / -5s
          //   L / →      seek +10s / +5s
          // Video.js's built-in `hotkeys: true` only ships Space/F/M —
          // we install our own handler so J/L (and arrow seek) are also
          // wired up. `event.key` is used (modern, keyboard-layout
          // independent) and we bail when a typeable element has focus
          // so this doesn't conflict with the comments composer.
          userActions: { doubleClick: false, hotkeys: handleHotkey },
          controlBar: { pictureInPictureToggle: false },
          poster: recording.poster_url || undefined,
        });
        playerRef.current = player;

        player.ready(() => {
          if (cancelled) return;
          // Hide the default Video.js bigPlayButton — our branded poster
          // overlay covers the idle state.
          try {
            player.bigPlayButton.hide();
          } catch (_) { /* harmless */ }

          installSkipButtons(videojs, player);
          attachWatchedBar(player, watchedBarHostRef);
          // Watched-bar host is now attached. Bump nonce so the
          // repaint effect runs against the actual DOM node — without
          // this, the effect that fires on initial state hydration
          // returns early because `watchedBarHostRef.current` was still
          // null at first React commit.
          setRepaintNonce((n) => n + 1);

          if (recording.source_kind === "hls") {
            try {
              player.hlsQualitySelector({ displayCurrentQuality: true });
            } catch (e) {
              console.warn("[LessonRecording] quality selector unavailable", e);
            }
          }

          player.src({
            src: recording.source_url,
            type: typeFor(recording.source_kind),
          });

          player.on("loadedmetadata", () => {
            const resume = Number(recording.resume_at_seconds) || 0;
            const dur = player.duration() || 0;
            if (resume > 0 && dur > 0 && resume < dur - 5) {
              try {
                player.currentTime(resume);
              } catch (_) { /* harmless */ }
            }
          });

          player.on("play", () => {
            setPlayerState("playing");
            if (firedLogRef.current) return;
            firedLogRef.current = true;
            if (recording.log_watch_url) {
              apiPost(recording.log_watch_url, {}).catch((err) => {
                console.warn("[LessonRecording] log_watch failed", err);
              });
            }
          });
          player.on("pause", () => {
            // Don't flip back to idle on a programmatic pause-at-load —
            // only flip to "paused" once we've actually started playing.
            setPlayerState((s) => (s === "playing" ? "paused" : s));
          });
          player.on("ended", () => {
            setPlayerState("ended");
            if (typeof onLessonEnded === "function") {
              try { onLessonEnded(); } catch (e) { /* harmless */ }
            }
          });
          player.on("timeupdate", handleTimeUpdate);
          player.on("error", () => {
            const err = (playerRef.current && playerRef.current.error()) || { message: "playback_error" };
            console.warn("[LessonRecording] player error", err);
            setErrorState(err);
            reportPlayerError({
              video_id: recording?.id,
              source_url: playerRef.current && playerRef.current.currentSrc(),
              error_code: err && err.code != null ? String(err.code) : "unknown",
              error_message: err && err.message ? err.message : "",
              position_seconds: (playerRef.current && playerRef.current.currentTime()) || 0,
            });
          });
          setLoading(false);
        });
      } catch (e) {
        if (!cancelled) {
          console.error("[LessonRecording] init failed", e);
          setErrorState({ message: e?.message || "init_failed" });
          setLoading(false);
        }
      }
    })();

    function handleTimeUpdate() {
      const p = playerRef.current;
      if (!p) return;
      const current = Math.floor(p.currentTime() || 0);

      // Reactive tick for chapter-highlighting — once per wall-clock
      // second, regardless of whether we ship a server-side tick.
      if (current !== lastTickedSecondRef.current) {
        lastTickedSecondRef.current = current;
        setCurrentSeconds(current);
      }

      if (!recording.timestamp_post_url) return;
      const duration = Math.floor(p.duration() || 0);
      if (!duration || !Number.isFinite(duration)) return;
      if (current === lastReportedSecondRef.current) return;
      // Only report at multiples of 5 (the worker's gating). Skip the
      // very first tick at second 0 — the legacy player also bails on
      // `timestamp > previous`, and reporting current=0 would create a
      // (-5, 0) bogus row server-side.
      if (current === 0) return;
      if (current % 5 !== 0) return;
      lastReportedSecondRef.current = current;

      apiGet(recording.timestamp_post_url, {
        headers: [
          { key: "timestamp", value: String(current) },
          { key: "duration", value: String(duration) },
          { key: "starttime", value: String(current) },
          { key: "endtime", value: String(current + 5) },
        ],
      })
        .then(({ statusCode, parsedResponse }) => {
          // The 5-second tick endpoint now returns the latest
          // server-authoritative progress fields (lesson_progress_percent,
          // watched_times, resume_at_seconds) for Lesson videos —
          // computed via the same `Lesson#progress(user)` /
          // `UserLesson#watch_times` methods the LessonDetailPresenter
          // uses on initial render. Hand them to the parent so it can
          // re-render the chip + watched-bar from a single source of
          // truth instead of running parallel client-side math.
          if (statusCode < 200 || statusCode >= 300) return;
          if (typeof onTickResponse !== "function") return;
          const data = parsedResponse || {};
          onTickResponse({
            percent:
              typeof data.lesson_progress_percent === "number"
                ? data.lesson_progress_percent
                : null,
            watched_times: Array.isArray(data.watched_times) ? data.watched_times : null,
          });
        })
        .catch((err) => {
          console.warn("[LessonRecording] timestamp post failed", err);
        });
    }

    // Custom Video.js userActions.hotkeys handler. Runs when the player
    // (or one of its children) has focus and a key is pressed. `this`
    // is bound to the Video.js player instance by Video.js itself.
    function handleHotkey(event) {
      // Bail when the user is typing into a form field elsewhere on the
      // page that nonetheless bubbles into the player (defensive — Video.js
      // already scopes hotkeys to its own element, but checking the
      // active element keeps us safe against future refactors).
      const active = (typeof document !== "undefined" && document.activeElement) || null;
      const tag = (active?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select" || active?.isContentEditable) return;
      // Don't intercept browser/OS shortcuts (Cmd+R, Ctrl+W, etc.).
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      const player = this;
      if (!player || typeof player.duration !== "function") return;
      const duration = player.duration() || 0;
      const time = player.currentTime() || 0;
      // `event.key` is the modern, keyboard-layout-independent API; supported
      // in all evergreen browsers (Chrome 51+, Firefox 23+, Safari 10.1+, Edge).
      // Lower-case so we don't have to test for both "F" and "f".
      const key = (event.key || "").toLowerCase();

      switch (key) {
        case " ":
        case "spacebar": // legacy Edge/IE
          event.preventDefault();
          if (player.paused()) player.play();
          else player.pause();
          break;
        case "f":
          event.preventDefault();
          if (player.isFullscreen()) player.exitFullscreen();
          else player.requestFullscreen();
          break;
        case "m":
          event.preventDefault();
          player.muted(!player.muted());
          break;
        case "j":
          event.preventDefault();
          player.currentTime(Math.max(0, time - 10));
          break;
        case "l":
          event.preventDefault();
          player.currentTime(duration ? Math.min(duration, time + 10) : time + 10);
          break;
        case "arrowleft":
          event.preventDefault();
          player.currentTime(Math.max(0, time - 5));
          break;
        case "arrowright":
          event.preventDefault();
          player.currentTime(duration ? Math.min(duration, time + 5) : time + 5);
          break;
        default:
          break;
      }
    }

    return () => {
      cancelled = true;
      if (playerRef.current && !playerRef.current.isDisposed_) {
        try { playerRef.current.dispose(); } catch (_) { /* harmless */ }
      }
      playerRef.current = null;
    };
  }, [recording?.id, recording?.source_url, retryNonce, canWatch]);

  // Sync the watched-bar host's children whenever the server-shipped
  // `watchedTimes` prop changes meaningfully (length grew or last
  // element shifted). `repaintNonce` is bumped by player.ready →
  // attachWatchedBar so this effect re-runs once the DOM host actually
  // exists (the first run during initial render returns early because
  // the host attaches async inside player.ready, after the React commit).
  const watchedLen = Array.isArray(watchedTimes) ? watchedTimes.length : 0;
  const watchedTail = watchedLen > 0 ? watchedTimes[watchedLen - 1] : null;
  React.useEffect(() => {
    const host = watchedBarHostRef.current;
    if (!host) return;
    host.replaceChildren();
    const arr = Array.isArray(watchedTimes) ? watchedTimes : [];
    // Dedupe + filter invalid percentages (e.g. legacy negative rows
    // before the `current === 0` guard ever shipped).
    const unique = Array.from(
      new Set(
        arr.filter((pct) => Number.isFinite(pct) && pct >= 0 && pct <= 100),
      ),
    ).sort((a, b) => a - b);
    unique.forEach((pct) => {
      const seg = document.createElement("div");
      seg.className = "tlc-watched-segment";
      seg.style.cssText = `position:absolute;top:0;height:100%;width:1.2%;left:${pct}%;background:${brand_orange_deep};opacity:0.85;border-radius:1px;pointer-events:none;`;
      host.appendChild(seg);
    });
  }, [watchedLen, watchedTail, repaintNonce]);

  // Subscription gate — render the upgrade panel where the player would go.
  if (!canWatch) {
    return (
      <Section id="lesson-recording" aria-labelledby="lesson-recording-title">
        <Inner>
          <Title id="lesson-recording-title">Materi Premium</Title>
          <SubscribePanel role="alert">
            <SubscribeIcon aria-hidden="true">🔒</SubscribeIcon>
            <div>
              <strong>Aktifkan langganan untuk menonton materi ini.</strong>
              <p>
                Akses lengkap ke seluruh materi sertifikasi tersedia melalui paket berlangganan
                TaxSam Learning.
              </p>
              <SubscribeButton href={membershipsUrl}>Lihat Paket Berlangganan</SubscribeButton>
            </div>
          </SubscribePanel>
        </Inner>
      </Section>
    );
  }

  if (!recording || !recording.source_url) {
    return (
      <Section id="lesson-recording" aria-labelledby="lesson-recording-title">
        <Inner>
          <Title id="lesson-recording-title">Materi</Title>
          <EmptyPanel>Video untuk materi ini belum tersedia.</EmptyPanel>
        </Inner>
      </Section>
    );
  }

  if (errorState) {
    return (
      <Section id="lesson-recording" aria-labelledby="lesson-recording-title">
        <Inner>
          <Title id="lesson-recording-title">Materi</Title>
          <ErrorPanel role="alert">
            <ErrorIcon aria-hidden="true">⚠️</ErrorIcon>
            <div>
              <strong>Video tidak dapat dimuat saat ini.</strong>
              <p>Tautan rekaman mungkin telah kedaluwarsa atau koneksi terputus. Silakan coba lagi.</p>
              <RetryButton
                type="button"
                onClick={() => {
                  setErrorState(null);
                  setLoading(true);
                  setRetryNonce((n) => n + 1);
                }}
              >
                Coba Lagi
              </RetryButton>
            </div>
          </ErrorPanel>
        </Inner>
      </Section>
    );
  }

  const handleJumpClick = (seconds) => () => {
    const p = playerRef.current;
    if (!p) return;
    try {
      p.currentTime(seconds);
      p.play();
      // Optimistic update so the active-chapter highlight snaps on
      // click instead of waiting for the next `timeupdate` (~250 ms).
      setCurrentSeconds(Math.floor(seconds));
    } catch (_) { /* harmless */ }
  };

  const handlePosterPlay = () => {
    const p = playerRef.current;
    if (!p) return;
    try {
      // ended → seek to 0 first
      if (playerState === "ended") p.currentTime(0);
      p.play();
    } catch (_) { /* harmless */ }
  };

  const showPosterOverlay = playerState === "idle" || playerState === "ended";

  // Active chapter = the highest jump-list entry whose `seconds` <= currentSeconds.
  // Jumplist is presenter-ordered ascending by seconds, so a single sweep is fine.
  let activeChapterIdx = -1;
  for (let i = 0; i < jumplist.length; i += 1) {
    if ((jumplist[i].seconds || 0) <= currentSeconds) activeChapterIdx = i;
    else break;
  }

  const stateMeta = STATE_META[playerState] || STATE_META.idle;

  return (
    <Section id="lesson-recording" aria-labelledby="lesson-recording-title">
      <Inner>
        <PlayerColumn>
          <StatusBar>
            <StateIndicator
              $state={playerState}
              title={stateMeta.label}
              aria-label={`Status pemutar: ${stateMeta.label}`}
            >
              <StateDot $state={playerState} />
            </StateIndicator>
            <KeyboardHint aria-hidden="true">
              <ShortcutGroup title="Putar / Jeda">
                <Kbd>Space</Kbd>
              </ShortcutGroup>
              <KbdSep>·</KbdSep>
              <ShortcutGroup title="Mundur 10 detik">
                <Kbd>J</Kbd>
                <KbdLabel>−10s</KbdLabel>
              </ShortcutGroup>
              <KbdSep>·</KbdSep>
              <ShortcutGroup title="Maju 10 detik">
                <Kbd>L</Kbd>
                <KbdLabel>+10s</KbdLabel>
              </ShortcutGroup>
              <KbdSep>·</KbdSep>
              <ShortcutGroup title="Layar Penuh">
                <Kbd>F</Kbd>
              </ShortcutGroup>
            </KeyboardHint>
            {durationSeconds > 0 && typeof lessonProgressPercent === "number" && (
              <ChipSlot>
                <LessonProgressChip
                  percent={lessonProgressPercent}
                  durationSeconds={durationSeconds}
                  variant="ring"
                />
              </ChipSlot>
            )}
          </StatusBar>
          <PlayerFrame $loading={loading} $state={playerState}>
            {loading && (
              <PlayerSkeleton aria-hidden="true">
                <SkeletonDisc>
                  <PlayTriangle />
                </SkeletonDisc>
                <SkeletonText>Memuat materi…</SkeletonText>
                <SkeletonShimmer />
              </PlayerSkeleton>
            )}
            <video
              ref={videoElRef}
              className="video-js vjs-big-play-centered vjs-default-skin"
              playsInline
              preload="metadata"
              data-testid="lesson-recording-player"
            />
            {showPosterOverlay && (
              <LessonPosterOverlay
                posterUrl={recording.poster_url}
                mode={playerState === "ended" ? "ended" : "idle"}
                onPlay={handlePosterPlay}
              />
            )}
          </PlayerFrame>
          {jumplist.length > 0 && (
            <ChapterStrip aria-label="Bab pelajaran">
              <ChapterStripHead>
                <ChapterStripTitle>
                  <ChapterStripIcon viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      d="M4 6h16M4 12h16M4 18h10"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </ChapterStripIcon>
                  Bab Pelajaran
                </ChapterStripTitle>
                <ChapterStripCount>{jumplist.length} bab</ChapterStripCount>
              </ChapterStripHead>
              <ChapterScroller>
                {jumplist.map((tm, idx) => {
                  const isActive = idx === activeChapterIdx;
                  const isPast = idx < activeChapterIdx;
                  return (
                    <ChapterCard
                      key={`${tm.seconds}-${idx}`}
                      type="button"
                      onClick={handleJumpClick(tm.seconds)}
                      $active={isActive}
                      $past={isPast}
                      aria-current={isActive ? "true" : undefined}
                    >
                      <ChapterIndex $active={isActive} $past={isPast}>
                        {isPast ? (
                          <CheckSvg viewBox="0 0 16 16" aria-hidden="true">
                            <path
                              d="M3 8.5l3 3 7-7"
                              stroke="currentColor"
                              strokeWidth="2.6"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              fill="none"
                            />
                          </CheckSvg>
                        ) : (
                          String(idx + 1).padStart(2, "0")
                        )}
                      </ChapterIndex>
                      <ChapterBody>
                        <ChapterTime>
                          {tm.formatted || formatHMS(tm.seconds)}
                          {isActive && <NowPlayingDot aria-hidden="true" />}
                        </ChapterTime>
                        <ChapterLabel $active={isActive}>{tm.label}</ChapterLabel>
                      </ChapterBody>
                    </ChapterCard>
                  );
                })}
              </ChapterScroller>
            </ChapterStrip>
          )}
        </PlayerColumn>
      </Inner>
    </Section>
  );
}

const STATE_META = {
  idle: { label: "Siap Diputar" },
  playing: { label: "Sedang Diputar" },
  paused: { label: "Dijeda" },
  ended: { label: "Selesai" },
};

export default React.memo(LessonRecording);

// ---- helpers ---------------------------------------------------------------

function typeFor(kind) {
  switch (kind) {
    case "hls":
      return "application/x-mpegURL";
    case "webm":
      return "video/webm";
    default:
      return "video/mp4";
  }
}

function formatHMS(secs) {
  const total = Math.max(0, Number(secs) || 0);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return [h, m, s].map((n) => String(n).padStart(2, "0")).join(":");
}

function installSkipButtons(videojs, player) {
  try {
    const Button = videojs.getComponent("Button");
    if (!Button) return;

    class SkipBackButton extends Button {
      constructor(p, options) {
        super(p, options);
        this.controlText("Mundur 10 detik");
        this.addClass("vjs-icon-replay-10");
      }
      handleClick() {
        const t = player.currentTime();
        player.currentTime(Math.max(0, t - 10));
      }
    }
    class SkipForwardButton extends Button {
      constructor(p, options) {
        super(p, options);
        this.controlText("Maju 10 detik");
        this.addClass("vjs-icon-forward-10");
      }
      handleClick() {
        const dur = player.duration() || 0;
        const t = player.currentTime();
        player.currentTime(Math.min(dur, t + 10));
      }
    }

    const controlBar = player.getChild("ControlBar");
    if (!controlBar) return;
    controlBar.addChild(new SkipBackButton(player), {}, 1);
    controlBar.addChild(new SkipForwardButton(player), {}, 2);
  } catch (e) {
    console.warn("[LessonRecording] skip buttons unavailable", e);
  }
}

// Inject a single child div inside Video.js's `.vjs-progress-control` and
// expose its DOM node back via `hostRef.current` so React can reactively
// repaint watched segments on each successful tick. The host sits ABOVE
// the buffer bar and BELOW the play-progress fill — visually merging the
// watched marks into the seek bar (YouTube-style heatmap).
function attachWatchedBar(player, hostRef) {
  try {
    const controlBar = player.getChild("ControlBar");
    if (!controlBar) return;
    const progressControl = controlBar.getChild("ProgressControl");
    const seekBar = progressControl?.getChild("SeekBar");
    if (!seekBar) return;
    const seekEl = seekBar.el();

    // Don't double-attach on retries.
    let host = seekEl.querySelector(".tlc-watched-bar");
    if (!host) {
      host = document.createElement("div");
      host.className = "tlc-watched-bar";
      host.setAttribute("aria-hidden", "true");
      host.style.cssText = [
        "position:absolute",
        "left:0",
        "right:0",
        "top:0",
        "bottom:0",
        "pointer-events:none",
        "z-index:1",
      ].join(";");
      // Insert as the first child so play-progress + mouse-display still
      // render on top.
      seekEl.insertBefore(host, seekEl.firstChild);
    }
    hostRef.current = host;
  } catch (e) {
    console.warn("[LessonRecording] watched-bar attach failed", e);
  }
}

// ---- styles ----------------------------------------------------------------

const Section = styled.section`
  /* Section is wrapped by Lesson.jsx ContentSection — no own bg/padding
     so the player blends into the 2-col grid container. */
`;

const Inner = styled.div``;

const Title = styled.h2`
  font-size: 22px;
  font-weight: 800;
  color: #1f2937;
  margin: 0 0 12px;
  [data-theme="dark"] & { color: #f3f4f6; }
`;

const PlayerColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

// Always-dark control-bar look — sits just above the player like a
// piece of player chrome regardless of the page's light/dark theme.
// No border-radius (matches the deck-style reference), thin top/bottom
// hairline borders for definition.
const StatusBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  border-radius: 0;
  background: #17171e;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  border-bottom: 1px solid rgba(0, 0, 0, 0.4);
  min-width: 0;

  @media ${narrowScreenMediaQuery} {
    padding: 8px 12px;
    gap: 8px;
  }
`;

// Compact state indicator — just the colored animated dot inside a
// small dark-translucent pill. The state label (Sedang Diputar / Dijeda
// / etc.) is exposed via `title` and `aria-label` for accessibility
// but not rendered as text — colour conveys state and frees horizontal
// room for the keyboard-shortcut hints.
const StateIndicator = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  flex-shrink: 0;
  cursor: help;
`;

const StateDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ $state }) =>
    $state === "playing" ? "#16a34a" :
    $state === "paused"  ? "#f59e0b" :
    $state === "ended"   ? brand_orange_deep :
                           "#9ca3af"};
  box-shadow: ${({ $state }) =>
    $state === "playing" ? "0 0 0 4px rgba(22, 163, 74, 0.18)" : "none"};
  ${({ $state }) =>
    $state === "playing"
      ? `animation: tlc-pulse 1.6s ease-in-out infinite;`
      : ""}

  @keyframes tlc-pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.7; transform: scale(0.92); }
  }
`;

const KeyboardHint = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  font-size: 11.5px;
  font-weight: 600;
  color: #e5e7eb;
  white-space: nowrap;
  justify-content: center;
  overflow: hidden;

  /* With the dot-only state indicator + circular % chip, the bar has
     ample room for the hint at the desktop 2-col layout. Hide it only
     on tablet/mobile where there's no physical keyboard anyway. */
  @media (max-width: 859px) { display: none; }
`;

const ShortcutGroup = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
`;

const Kbd = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  padding: 2px 7px;
  border-radius: 6px;
  background: #f3f4f6;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-bottom: 2px solid rgba(0, 0, 0, 0.5);
  font-family: "JetBrains Mono", "SFMono-Regular", Menlo, monospace;
  font-size: 10.5px;
  font-weight: 800;
  color: #111827;
  letter-spacing: 0;
  flex-shrink: 0;
`;

const KbdLabel = styled.span`
  font-weight: 700;
  color: inherit;
  font-variant-numeric: tabular-nums;
`;

const KbdSep = styled.span`
  color: rgba(255, 255, 255, 0.28);
  font-weight: 700;
  flex-shrink: 0;
`;

const ChipSlot = styled.div`
  margin-left: auto;
  flex-shrink: 0;
  min-width: 0;
`;

const PlayerFrame = styled.div`
  position: relative;
  background: #000;
  border-radius: 18px;
  overflow: hidden;
  aspect-ratio: 16 / 9;
  box-shadow:
    0 0 0 1px rgba(216, 89, 7, 0.18),
    0 24px 48px -12px rgba(216, 89, 7, 0.18),
    0 18px 40px rgba(15, 23, 42, 0.18);
  transition: box-shadow 240ms ease;

  ${({ $state }) =>
    $state === "playing"
      ? `
    box-shadow:
      0 0 0 1px rgba(216, 89, 7, 0.32),
      0 28px 60px -12px rgba(216, 89, 7, 0.32),
      0 18px 44px rgba(15, 23, 42, 0.22);
  `
      : ""}

  & .video-js {
    width: 100%;
    height: 100%;
  }

  [data-theme="dark"] & {
    box-shadow:
      0 0 0 1px rgba(216, 89, 7, 0.28),
      0 28px 60px -12px rgba(216, 89, 7, 0.24),
      0 18px 44px rgba(0, 0, 0, 0.6);
  }
`;

const ChapterStrip = styled.section`
  position: relative;
  background: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 16px;
  padding: 16px 18px 18px;
  display: flex;
  flex-direction: column;
  gap: 14px;

  [data-theme="dark"] & {
    background: rgba(255, 255, 255, 0.03);
    border-color: rgba(255, 255, 255, 0.08);
  }
`;

const ChapterStripHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const ChapterStripTitle = styled.h3`
  margin: 0;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${brand_orange_deep};
`;

const ChapterStripIcon = styled.svg`
  width: 16px;
  height: 16px;
`;

const ChapterStripCount = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: #6b7280;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  font-variant-numeric: tabular-nums;

  [data-theme="dark"] & { color: #9ca3af; }
`;

const ChapterScroller = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(220px, 1fr);
  gap: 10px;
  overflow-x: auto;
  padding-bottom: 4px;
  scroll-behavior: smooth;
  scrollbar-width: thin;
  scrollbar-color: rgba(216, 89, 7, 0.32) transparent;

  &::-webkit-scrollbar { height: 6px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb {
    background: rgba(216, 89, 7, 0.32);
    border-radius: 999px;
  }

  /* On 720+px keep cards in a comfortable strip; on narrow widths shrink
     the min so 2 cards fit per visible viewport before requiring scroll. */
  @media (max-width: 1023px) {
    grid-auto-columns: minmax(190px, 1fr);
  }

  @media ${narrowScreenMediaQuery} {
    grid-auto-columns: 78%;
  }
`;

const ChapterCard = styled.button`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  text-align: left;
  padding: 12px 14px;
  border-radius: 12px;
  background: ${({ $active, $past }) =>
    $active
      ? "linear-gradient(135deg, rgba(216, 89, 7, 0.10), rgba(247, 179, 12, 0.08))"
      : $past
        ? "rgba(16, 185, 129, 0.06)"
        : "rgba(15, 23, 42, 0.025)"};
  border: 1px solid ${({ $active, $past }) =>
    $active
      ? "rgba(216, 89, 7, 0.45)"
      : $past
        ? "rgba(16, 185, 129, 0.28)"
        : "rgba(15, 23, 42, 0.08)"};
  cursor: pointer;
  transition: transform 160ms ease, border-color 160ms ease, background 160ms ease;
  min-width: 0;

  &:hover {
    transform: translateY(-2px);
    border-color: rgba(216, 89, 7, 0.5);
  }
  &:focus-visible {
    outline: 2px solid #fdba74;
    outline-offset: 2px;
  }

  [data-theme="dark"] & {
    background: ${({ $active, $past }) =>
      $active
        ? "rgba(216, 89, 7, 0.18)"
        : $past
          ? "rgba(16, 185, 129, 0.12)"
          : "rgba(255, 255, 255, 0.04)"};
    border-color: ${({ $active, $past }) =>
      $active
        ? "rgba(216, 89, 7, 0.5)"
        : $past
          ? "rgba(16, 185, 129, 0.32)"
          : "rgba(255, 255, 255, 0.08)"};
  }
`;

const ChapterIndex = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${({ $active, $past }) =>
    $active
      ? "linear-gradient(135deg, #d85907, #f7b30c)"
      : $past
        ? "#10b981"
        : "rgba(15, 23, 42, 0.06)"};
  color: ${({ $active, $past }) => ($active || $past ? "#fff" : "#4b5563")};
  font-weight: 800;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  box-shadow: ${({ $active }) =>
    $active ? "0 4px 12px rgba(216, 89, 7, 0.32)" : "none"};

  [data-theme="dark"] & {
    color: ${({ $active, $past }) => ($active || $past ? "#fff" : "#cbd5e1")};
    background: ${({ $active, $past }) =>
      $active
        ? "linear-gradient(135deg, #d85907, #f7b30c)"
        : $past
          ? "#10b981"
          : "rgba(255, 255, 255, 0.06)"};
  }
`;

const CheckSvg = styled.svg`
  width: 14px;
  height: 14px;
`;

const ChapterBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1;
`;

const ChapterTime = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-variant-numeric: tabular-nums;
  font-weight: 700;
  color: ${brand_orange_deep};
  font-size: 12px;
  letter-spacing: 0.02em;
`;

const NowPlayingDot = styled.span`
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #16a34a;
  box-shadow: 0 0 0 4px rgba(22, 163, 74, 0.18);
  animation: tlc-pulse 1.6s ease-in-out infinite;
`;

const ChapterLabel = styled.span`
  color: ${({ $active }) => ($active ? "#1f2937" : "#374151")};
  font-size: 13px;
  font-weight: ${({ $active }) => ($active ? 700 : 600)};
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  [data-theme="dark"] & { color: ${({ $active }) => ($active ? "#f3f4f6" : "#cbd5e1")}; }
`;

const PlayerSkeleton = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  background:
    radial-gradient(60% 60% at 50% 40%, rgba(216, 89, 7, 0.18), transparent 65%),
    linear-gradient(135deg, #1f2937, #0f172a);
  z-index: 1;
`;

const SkeletonDisc = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, rgba(216, 89, 7, 0.6), rgba(247, 179, 12, 0.4));
  box-shadow: 0 12px 32px rgba(216, 89, 7, 0.32);
  animation: tlc-disc-pulse 1.6s ease-in-out infinite;

  @keyframes tlc-disc-pulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.06); }
  }
`;

const PlayTriangle = styled.span`
  width: 0;
  height: 0;
  border-left: 14px solid #fff;
  border-top: 9px solid transparent;
  border-bottom: 9px solid transparent;
  margin-left: 4px;
`;

const SkeletonText = styled.span`
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.7);
`;

const SkeletonShimmer = styled.div`
  width: 60%;
  height: 6px;
  border-radius: 999px;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.05) 0%,
    rgba(255, 255, 255, 0.22) 50%,
    rgba(255, 255, 255, 0.05) 100%
  );
  background-size: 200% 100%;
  animation: shimmer 1.4s linear infinite;

  @keyframes shimmer {
    from { background-position: 200% 0; }
    to { background-position: -200% 0; }
  }
`;

const SubscribePanel = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 24px 28px;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 16px;
  color: #92400e;

  strong {
    display: block;
    font-size: 17px;
    margin-bottom: 4px;
    color: ${brand_orange_deep};
  }
  p { margin: 0 0 12px; font-size: 14px; line-height: 1.5; }

  [data-theme="dark"] & {
    background: rgba(216, 89, 7, 0.08);
    border-color: rgba(216, 89, 7, 0.32);
    color: #fde68a;
  }
`;

const SubscribeIcon = styled.span`
  font-size: 28px;
  line-height: 1;
`;

const SubscribeButton = styled.a`
  display: inline-block;
  padding: 10px 22px;
  border-radius: 999px;
  background: ${brand_orange_deep};
  color: #fff;
  font-weight: 700;
  font-size: 13px;
  text-decoration: none;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  transition: filter 150ms ease;

  &:hover { filter: brightness(0.95); }
  &:focus-visible { outline: 2px solid #fb923c; outline-offset: 3px; }
`;

const EmptyPanel = styled.div`
  padding: 22px 24px;
  background: #f9fafb;
  border: 1px dashed #e5e7eb;
  border-radius: 12px;
  color: #6b7280;
  text-align: center;
  font-size: 14px;

  [data-theme="dark"] & {
    background: rgba(255, 255, 255, 0.04);
    border-color: rgba(255, 255, 255, 0.1);
    color: #9ca3af;
  }
`;

const ErrorPanel = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 20px 24px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 16px;
  color: #991b1b;
  line-height: 1.5;

  strong {
    display: block;
    font-size: 16px;
    margin-bottom: 4px;
    color: ${brand_danger};
  }
  p { margin: 0 0 12px; font-size: 14px; }

  [data-theme="dark"] & {
    background: rgba(220, 38, 38, 0.08);
    border-color: rgba(220, 38, 38, 0.24);
    color: #fca5a5;
    strong { color: #fca5a5; }
  }
`;

const ErrorIcon = styled.span`
  font-size: 24px;
  line-height: 1;
  flex-shrink: 0;
`;

const RetryButton = styled.button`
  display: inline-block;
  padding: 8px 18px;
  border-radius: 10px;
  border: none;
  background: ${brand_orange_deep};
  color: #fff;
  font-weight: 700;
  font-size: 13px;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  transition: filter 150ms ease, transform 100ms ease;

  &:hover { filter: brightness(0.95); }
  &:active { transform: scale(0.98); }
  &:focus-visible {
    outline: 2px solid ${brand_orange_deep};
    outline-offset: 3px;
  }
`;

